/**
 * Get Sessions Use Case
 * Business logic for fetching and managing charging sessions
 */

import { SessionsReportRepository } from '../data/sessions-report-repository';
import { SessionTransformer } from '../transformers/SessionTransformer';
import { SessionGridRow, SessionDetail, SessionFilters } from '../types/session-types';
import { ChargeSessionApiModel } from '../types/api-types';

export interface GetSessionsResult {
  sessions: SessionGridRow[];
  totalCount: number;
}

export interface GetSessionDetailResult {
  detail: SessionDetail;
}

export class GetSessionsUseCase {
  constructor(private readonly repository: SessionsReportRepository) {}

  /**
   * Execute: Get list of charging sessions with all filters applied
   */
  async execute(filters: SessionFilters): Promise<GetSessionsResult> {
    try {
      const response = await this.repository.getChargingSessionsByFilters(filters);

      const sessions = SessionTransformer.toGridRows(
        response.value as ChargeSessionApiModel[]
      );

      return {
        sessions,
        totalCount: response['@odata.count'] ?? sessions.length,
      };
    } catch (error) {
      console.error('GetSessionsUseCase.execute failed:', error);
      throw new Error(
        error instanceof Error
          ? error.message
          : 'Failed to load charging sessions. Please try again.'
      );
    }
  }

  /**
   * Get session detail with optional breakdown
   */
  async getSessionDetail(
    session: SessionGridRow,
    includeBreakdown: boolean = true
  ): Promise<GetSessionDetailResult> {
    try {
      // Fetch real session data from API and breakdown in parallel
      const [apiModel, breakdown] = await Promise.all([
        this.repository.getSessionById(session.sessionId),
        includeBreakdown
          ? this.repository.getSessionBreakdown(session.machineId, session.sessionId)
              .catch((err) => { console.warn('Failed to load session breakdown:', err); return undefined; })
          : Promise.resolve(undefined),
      ]);

      if (!apiModel) {
        throw new Error(`Session ${session.sessionId} not found`);
      }

      const detail = SessionTransformer.toDetail(apiModel, breakdown);
      return { detail };
    } catch (error) {
      console.error('GetSessionsUseCase.getSessionDetail failed:', error);
      throw new Error(
        error instanceof Error
          ? error.message
          : 'Failed to load session details. Please try again.'
      );
    }
  }

  /**
   * Load paginated sessions with separate count query
   * Makes two parallel API calls: one for data (count=false), one for count (count=true)
   */
  async loadPage(
    filters: SessionFilters,
    page: number,
    pageSize: number = 50
  ): Promise<GetSessionsResult> {
    try {
      // Make both requests in parallel for better performance
      const [response, totalCount] = await Promise.all([
        // Data query with FULL filters (count=false)
        this.repository.getChargingSessionsByFilters(
          filters,
          {
            top: pageSize,
            skip: page * pageSize,
          }
        ),
        // Count query with FULL filters (count=true)
        this.repository.getChargingSessionsCountByFilters(filters),
      ]);

      const sessions = SessionTransformer.toGridRows(
        response.value as ChargeSessionApiModel[]
      );

      return {
        sessions,
        totalCount,
      };
    } catch (error) {
      console.error('GetSessionsUseCase.loadPage failed:', error);
      throw new Error(
        error instanceof Error
          ? error.message
          : 'Failed to load charging sessions. Please try again.'
      );
    }
  }
}
